import { useEffect, useMemo, useState } from "react";

// Head column name (lowercase, single spaces) → key used in a report's field map.
export const normalizeColumnName = (name) =>
  String(name ?? "").trim().replace(/\s+/g, " ").toLowerCase();

// A field may hold a list (e.g. Sale Monitoring's skus); each entry is its own option.
const getValues = (row, field) => {
  const value = row?.[field];
  const list = Array.isArray(value) ? value : [value];
  const values = list.map((entry) => String(entry ?? "").trim());
  return values.length ? values : [""];
};

const compareNames = (a, b) => a.localeCompare(b, undefined, { sensitivity: "base", numeric: true });

/**
 * Client-side value filters for the column filter menus in DailySaleReportTableHead, like the
 * My Inventory column filters. `fieldsByColumn` maps a head column name (see
 * normalizeColumnName, e.g. "customer", "sku", "stock id") to the row field it shows.
 * Filters on several columns combine. Only the rows already loaded (the current page) are
 * filtered, and the selections reset when the rows reload (new page, date or search).
 * Pass the result to the head as `columnFilters` and sort `.rows` instead of the raw rows.
 */
export const useReportColumnFilters = (rows, fieldsByColumn) => {
  // column key → selected values; a missing key means no filter.
  const [selected, setSelected] = useState({});

  useEffect(() => {
    setSelected({});
  }, [rows]);

  const fieldsKey = JSON.stringify(fieldsByColumn);
  const optionsByColumn = useMemo(() => {
    const fields = JSON.parse(fieldsKey);
    const result = {};
    Object.entries(fields).forEach(([column, field]) => {
      const values = new Set();
      // Empty cells get no option, so filtering on a column hides the rows left blank in it.
      (rows || []).forEach((row) =>
        getValues(row, field).forEach((value) => value && values.add(value))
      );
      result[column] = [...values].sort(compareNames);
    });
    return result;
  }, [rows, fieldsKey]);

  const filteredRows = useMemo(() => {
    if (!Array.isArray(rows)) return [];
    const active = Object.entries(selected).map(([column, values]) => [
      fieldsByColumn[column],
      new Set(values),
    ]);
    if (!active.length) return rows;
    return rows.filter((row) =>
      active.every(([field, values]) => getValues(row, field).some((value) => values.has(value)))
    );
    // fieldsByColumn is read through fieldsKey so an inline object doesn't refilter each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, fieldsKey, selected]);

  const getFilter = (columnName) => {
    const column = normalizeColumnName(columnName);
    if (!optionsByColumn[column]) return null;
    return {
      options: optionsByColumn[column],
      selected: selected[column] ?? null,
      setSelected: (values) =>
        setSelected((prev) => {
          const next = { ...prev };
          if (values) next[column] = values;
          else delete next[column];
          return next;
        }),
    };
  };

  return { rows: filteredRows, getFilter };
};
